import { useState, useEffect } from "react";
import { useAdminLanguage } from "../../layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../../components/ui/table";
import {
  Activity, Cpu, DollarSign, AlertTriangle, TrendingUp, Clock, RefreshCw, Zap
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899"];

export default function AdminAIMonitor() {
  const { t, language } = useAdminLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/ai-monitor`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Failed to fetch AI monitor data:", e);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const { summary, by_service, daily, recent } = data;

  const pieData = by_service.map((s, i) => ({
    name: `${s.service} ${s.operation}`,
    value: s.count,
    color: COLORS[i % COLORS.length]
  }));

  const dateLocale = language === "es" ? "es-ES" : language === "en" ? "en-US" : "fr-FR";

  return (
    <div className="space-y-6" data-testid="admin-ai-monitor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">
            {t("ai_monitor_title")}
          </h1>
          <p className="text-sm text-zinc-500">{t("ai_monitor_subtitle")}</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-ai-monitor">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t("refresh")}
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-900/30">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">{t("total_calls")}</p>
                <p className="text-xl font-bold text-white">{summary.total_calls}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-900/30">
                <Zap className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">{t("today_calls")}</p>
                <p className="text-xl font-bold text-white">{summary.today_calls}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-900/30">
                <DollarSign className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">{t("month_cost")}</p>
                <p className="text-xl font-bold text-white">${summary.month_cost}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-900/30">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-xs text-zinc-500">{t("error_rate")}</p>
                <p className="text-xl font-bold text-white">{summary.error_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t("daily_usage")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {daily.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 10 }} />
                  <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8, color: "#fff" }} />
                  <Line type="monotone" dataKey="calls" stroke="#3B82F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-zinc-500 text-sm">
                {t("no_ai_data")}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Cpu className="w-4 h-4" />
              {t("cost_distribution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-zinc-500 text-sm">
                {t("no_ai_data")}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Service Details Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">{t("usage_by_service")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("service")}</TableHead>
                  <TableHead>{t("operation")}</TableHead>
                  <TableHead className="text-right">{t("calls")}</TableHead>
                  <TableHead className="text-right">{t("cost")}</TableHead>
                  <TableHead className="text-right hidden sm:table-cell">{t("avg_latency")}</TableHead>
                  <TableHead className="text-right">{t("success_rate")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {by_service.length > 0 ? by_service.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">{s.service}</Badge>
                    </TableCell>
                    <TableCell className="capitalize">{s.operation}</TableCell>
                    <TableCell className="text-right font-mono">{s.count}</TableCell>
                    <TableCell className="text-right font-mono">${s.total_cost}</TableCell>
                    <TableCell className="text-right font-mono hidden sm:table-cell">{s.avg_duration_ms}ms</TableCell>
                    <TableCell className="text-right">
                      <Badge className={s.success_rate >= 95 ? "bg-green-900/30 text-green-400" : "bg-red-900/30 text-red-400"}>
                        {s.success_rate}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      {t("no_ai_data")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Calls */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {t("recent_calls")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("date")}</TableHead>
                  <TableHead>{t("service")}</TableHead>
                  <TableHead>{t("operation")}</TableHead>
                  <TableHead className="text-right">{t("cost")}</TableHead>
                  <TableHead className="text-right">{t("status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.length > 0 ? recent.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs text-zinc-500">
                      {new Date(r.created_at).toLocaleString(dateLocale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize text-xs">{r.service}</Badge>
                    </TableCell>
                    <TableCell className="capitalize text-sm">{r.operation}</TableCell>
                    <TableCell className="text-right font-mono text-sm">${r.estimated_cost}</TableCell>
                    <TableCell className="text-right">
                      {r.success ? (
                        <Badge className="bg-green-900/30 text-green-400 text-xs">OK</Badge>
                      ) : (
                        <Badge className="bg-red-900/30 text-red-400 text-xs">Error</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                      {t("no_ai_data")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
